import { supabase } from './supabase'
import { viewingAs } from './viewAsState'

/**
 * 301 (David, 19 Oct): how the hub is used - the counting half and the read.
 *
 * ── WHAT THIS DEVICE SENDS, AND WHEN ─────────────────────────────────────────
 *
 * Two counts, each decided HERE, 198's shape (lib/videoViews.ts): the server
 * cannot dedupe without remembering who called, which is the per-person record
 * rule 8 forbids, so the browser remembers what it has counted and nothing
 * about the person leaves it.
 *
 *   a VISIT  once per device per UTC day (the day the database files it under):
 *            the network country (/api/where - Netlify's geolocation, see
 *            netlify/edge-functions/where.ts), the platform, and whether the
 *            hub is the installed app or a browser tab.
 *   an OPEN  once per device per LOCAL hour per local day, filed by the
 *            database under the week, never the day (301's header: an hour
 *            on a given day can name somebody next to 197's sign-in times).
 *
 * One key each in localStorage, OVERWRITTEN rather than accumulated - a key
 * per day would leave a year of dates on a shared tablet. Storage that throws
 * falls back to once per page. A failed send is not remembered on disk.
 *
 * View as (176) never counts: it is somebody else's screen, and the server
 * would refuse the write anyway (read-only).
 */

export type Platform = 'ios' | 'android' | 'windows' | 'macos' | 'other'
export type DisplayMode = 'app' | 'browser'

export const VISIT_KEY = 'oneconetec.usage.visit'
export const OPEN_KEY = 'oneconetec.usage.open'

/** iPadOS reports itself as a Mac; a Mac with a touch screen is an iPad. */
export function platformOf(ua: string, maxTouchPoints = 0): Platform {
  if (/iphone|ipad|ipod/i.test(ua)) return 'ios'
  if (/android/i.test(ua)) return 'android'
  if (/windows/i.test(ua)) return 'windows'
  if (/macintosh|mac os x/i.test(ua)) return maxTouchPoints > 1 ? 'ios' : 'macos'
  return 'other'
}

/** Installed (home screen, desktop app window) or a tab. iOS Safari says so on navigator. */
export function displayModeOf(standaloneQuery: boolean, iosStandalone: boolean | undefined): DisplayMode {
  return standaloneQuery || iosStandalone === true ? 'app' : 'browser'
}

/** The UTC date the database files a visit under (rule 9). */
export function utcDay(d: Date = new Date()): string {
  return d.toISOString().slice(0, 10)
}

/** This device's local date and hour, e.g. 2026-10-19T07. */
export function localHourKey(d: Date = new Date()): string {
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}`
}

/** The network country, or ZZ (unknown) - never throws, gives up after a second and a half. */
export async function fetchCountry(f: typeof fetch = fetch, timeoutMs = 1500): Promise<string> {
  const ctl = typeof AbortController !== 'undefined' ? new AbortController() : null
  const t = ctl ? setTimeout(() => ctl.abort(), timeoutMs) : null
  try {
    const r = await f('/api/where', { signal: ctl?.signal, cache: 'no-store' })
    if (!r.ok) return 'ZZ'
    const body = (await r.json()) as { country?: unknown }
    return typeof body.country === 'string' && /^[A-Z]{2}$/.test(body.country) ? body.country : 'ZZ'
  } catch {
    return 'ZZ'
  } finally {
    if (t) clearTimeout(t)
  }
}

function stored(key: string): string | null {
  try {
    return window.localStorage.getItem(key)
  } catch {
    return null
  }
}

function store(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value)
  } catch {
    /* No storage: once per page is the best this device can do. */
  }
}

const triedThisPage = new Set<string>()

export type VisitFacts = { country: string; platform: Platform; displayMode: DisplayMode }

export type UsageSenders = {
  visit: (v: VisitFacts) => Promise<void>
  open: (localHour: number) => Promise<void>
  facts: () => Promise<VisitFacts>
}

async function sendVisit(v: VisitFacts): Promise<void> {
  const { error } = await supabase.rpc('record_hub_visit', {
    p_country: v.country,
    p_platform: v.platform,
    p_display_mode: v.displayMode,
  })
  if (error) throw error
}

async function sendOpen(localHour: number): Promise<void> {
  const { error } = await supabase.rpc('record_hub_open', { p_local_hour: localHour })
  if (error) throw error
}

async function facts(): Promise<VisitFacts> {
  const nav = typeof navigator !== 'undefined' ? navigator : undefined
  const standalone =
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia('(display-mode: standalone)').matches ||
        window.matchMedia('(display-mode: fullscreen)').matches ||
        window.matchMedia('(display-mode: minimal-ui)').matches
      : false
  return {
    country: await fetchCountry(),
    platform: platformOf(nav?.userAgent ?? '', nav?.maxTouchPoints ?? 0),
    displayMode: displayModeOf(standalone, (nav as { standalone?: boolean } | undefined)?.standalone),
  }
}

const LIVE: UsageSenders = { visit: sendVisit, open: sendOpen, facts }

/**
 * Count this device's visit for today and its open for this hour, each if not
 * already counted. Returns what was sent. Never throws.
 */
export async function countUsage(
  now: Date = new Date(),
  send: UsageSenders = LIVE,
): Promise<{ visit: boolean; open: boolean }> {
  const out = { visit: false, open: false }
  if (viewingAs()) return out

  const day = utcDay(now)
  const visitTag = `visit:${day}`
  if (!triedThisPage.has(visitTag) && stored(VISIT_KEY) !== day) {
    triedThisPage.add(visitTag)
    try {
      await send.visit(await send.facts())
      store(VISIT_KEY, day)
      out.visit = true
    } catch {
      /* A missing number, not an error anybody should see. */
    }
  }

  const hour = localHourKey(now)
  const openTag = `open:${hour}`
  if (!triedThisPage.has(openTag) && stored(OPEN_KEY) !== hour) {
    triedThisPage.add(openTag)
    try {
      await send.open(now.getHours())
      store(OPEN_KEY, hour)
      out.open = true
    } catch {
      /* as above */
    }
  }
  return out
}

/**
 * Start counting for a signed-in person: now, and each time the hub comes back
 * to the front (a phone app is rarely reloaded - it is resumed). Returns the
 * cleanup for the effect that calls it.
 */
/* ── 327 (CMP-11): arrivals from an email, counted with no person ──────────
   A link in the Monday email carries ?from=digest, a notice ?from=email. The
   source is noted on the first load of the page (sessionStorage, so it
   survives signing in on the same tab) and, once signed in, this device adds
   one to today's arrivals for that source if it has not already. Opens and
   clicks are never tracked by the mail provider (rule 8); this is the reach
   number, and it has no person in it. */
export type ArrivalSource = 'digest' | 'email'
const ARRIVAL_PENDING = 'oneconetec.usage.arrival'
const ARRIVAL_KEY = (src: ArrivalSource) => `oneconetec.usage.arrival.${src}`

export function arrivalSource(search: string): ArrivalSource | null {
  const from = new URLSearchParams(search).get('from')
  return from === 'digest' || from === 'email' ? from : null
}

function noteArrival(): void {
  if (typeof window === 'undefined') return
  const src = arrivalSource(window.location.search)
  if (!src) return
  try {
    window.sessionStorage.setItem(ARRIVAL_PENDING, src)
  } catch {
    /* no storage: this arrival goes uncounted */
  }
}
noteArrival()

export async function countArrival(
  now: Date = new Date(),
  send: (src: ArrivalSource) => Promise<void> = async (src) => {
    const { error } = await supabase.rpc('record_hub_arrival', { p_source: src })
    if (error) throw error
  },
): Promise<ArrivalSource | null> {
  if (viewingAs()) return null
  let src: ArrivalSource | null = null
  try {
    const v = window.sessionStorage.getItem(ARRIVAL_PENDING)
    src = v === 'digest' || v === 'email' ? v : null
    window.sessionStorage.removeItem(ARRIVAL_PENDING)
  } catch {
    return null
  }
  if (!src) return null
  const day = utcDay(now)
  if (stored(ARRIVAL_KEY(src)) === day) return null
  try {
    await send(src)
    store(ARRIVAL_KEY(src), day)
    return src
  } catch {
    return null
  }
}

export function startUsageCounting(): () => void {
  void countUsage()
  void countArrival()
  const onVisible = () => {
    if (document.visibilityState === 'visible') void countUsage()
  }
  document.addEventListener('visibilitychange', onVisible)
  return () => document.removeEventListener('visibilitychange', onVisible)
}

/* ── The read: usage_dashboard() ─────────────────────────────────────────── */

/** A people count the server held back because it was 1-4 (or a visit count 1-9). */
export type Held = number | null

export type UsageGroup = {
  /** null = the groups too small to name, added together. */
  group: string | null
  staff: number
  can_sign_in: Held
  signed_in: Held
  active_7d: Held
  with_photo: Held
}

export type UsageDay = {
  day: string
  sign_ins: number
  first_sign_ins: number
  /** People active that UTC day - null before the nightly snapshot existed. */
  active: number | null
  visits: number
  app_visits: number
}

export type UsageData = {
  days: number
  from: string
  today: string
  generated_at: string
  k_people: number
  k_visits: number
  kpis: {
    staff: number
    can_sign_in: number
    signed_in_ever: number
    active_24h: number
    active_7d: number
    active_30d: number
    with_photo: number
    sign_ins_24h: number
    first_sign_ins_7d: number
    visits_today: number
  }
  trend: UsageDay[]
  by_country: UsageGroup[]
  by_brand: UsageGroup[]
  network: { countries: { country: string; visits: number }[]; other: Held; unknown: Held; total: number }
  devices: {
    people: { os: Platform; form: 'phone' | 'tablet' | 'computer'; people: Held }[]
    people_total: number
    modes: { platform: Platform; display_mode: DisplayMode; visits: Held }[]
  }
  features: {
    daily: { day: string; key: FeatureKey; n: number }[]
    people: { key: FeatureKey; n: number; people: Held }[]
  }
  hours: { opens: { hour: number; n: number }[]; sign_ins: { hour: number; n: number }[] }
}

export const FEATURE_KEYS = [
  'posts',
  'recognitions',
  'replies',
  'one_question',
  'ask_one',
  'video_started',
  'contest_entries',
  'support_questions',
] as const
export type FeatureKey = (typeof FEATURE_KEYS)[number]

export const PERIODS = [7, 30, 90] as const

/** Whole-company analytics.read only; anyone else is refused by the server. */
export async function loadUsage(days: number): Promise<UsageData> {
  const { data, error } = await supabase.rpc('usage_dashboard', { p_days: days })
  if (error) throw error
  return data as UsageData
}

/** Feature totals over the period, every key present, in FEATURE_KEYS order. */
export function featureTotals(d: Pick<UsageData, 'features'>): { key: FeatureKey; n: number; people: Held | undefined }[] {
  const n = new Map<string, number>()
  for (const r of d.features.daily) n.set(r.key, (n.get(r.key) ?? 0) + r.n)
  const people = new Map(d.features.people.map((p) => [p.key, p.people]))
  return FEATURE_KEYS.map((key) => ({ key, n: n.get(key) ?? 0, people: people.has(key) ? people.get(key)! : undefined }))
}

/** "app" share of visits in the period, 0-100, or null with no visits. */
export function appShare(trend: readonly UsageDay[]): number | null {
  let all = 0
  let app = 0
  for (const t of trend) {
    all += t.visits
    app += t.app_visits
  }
  return all > 0 ? Math.round((app * 100) / all) : null
}

/** A count for the page: a held-back number says so instead of pretending to be zero. */
export function heldText(n: Held | undefined, k: number, fmt: (n: number) => string = String): string {
  if (n === undefined) return '0'
  return n === null ? `fewer than ${k}` : fmt(n)
}

/** a of b as a whole percentage, or '' when either is held back or b is 0. */
export function pctOf(a: Held, b: Held): string {
  if (a === null || b === null || !b) return ''
  return `${Math.round((a * 100) / b)}%`
}
